import axios, { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { reportError } from 'src/modules/logs/Sentry';
import type {
  StarterFreezeResponse,
  StreakClaimResponse,
  StreakDay,
  StreakErrorCode,
  StreakGoalRead,
  StreakSummary,
} from 'src/types/streak.types';
import { queryKeys } from 'src/queries/keys';

export const useStreakSummary = ({ isEnabled = true } = {}) =>
  useQuery<StreakSummary>({
    queryKey: queryKeys.streak(),
    enabled: isEnabled,
    queryFn: async () =>
      (await axios.get<StreakSummary>('/api/user/streak')).data,
    staleTime: 30_000,
  });

export const useStreakDays = (
  start: string,
  end: string,
  { isEnabled = true, keepPreviousData = false } = {},
) =>
  useQuery<StreakDay[]>({
    queryKey: queryKeys.streakDays(start, end),
    enabled: isEnabled,
    keepPreviousData,
    queryFn: async () =>
      (
        await axios.get<StreakDay[]>('/api/user/streak/days', {
          params: { start, end },
        })
      ).data,
    staleTime: 30_000,
  });

export const freezeDatesOf = (days: StreakDay[] | undefined): Set<string> =>
  new Set((days ?? []).filter(day => day.kind === 'FREEZE').map(day => day.date));

type StreakErrorBody = { message?: { code?: StreakErrorCode } | string };

export const asStreakError = (error: unknown): StreakErrorCode | null => {
  const detail = (error as AxiosError<StreakErrorBody> | undefined)?.response?.data
    ?.message;

  return typeof detail === 'object' && detail !== null && detail.code
    ? detail.code
    : null;
};

export const useChooseStreakGoalMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<StreakGoalRead, AxiosError<StreakErrorBody>, { target_days: number }>({
    mutationFn: async body =>
      (await axios.post<StreakGoalRead>('/api/user/streak/goal', body)).data,
    onSuccess: async goal => {
      const previous = queryClient.getQueryData<StreakSummary>(
        queryKeys.streak(),
      );

      if (previous !== undefined) {
        queryClient.setQueryData<StreakSummary>(queryKeys.streak(), {
          ...previous,
          goal,
          current_length: goal.baseline_length,
        });
      }

      void Promise.all([
        queryClient.invalidateQueries(queryKeys.streak()),
        queryClient.invalidateQueries(queryKeys.wallet()),
      ]);
    },
    onError: (error, variables) => {
      reportError('Fail to choose streak goal', error);
      void logAnalyticsEvent('streak_goal_failed', {
        goal_days: variables.target_days,
        error_type: asStreakError(error) ?? 'unknown',
      });

      void queryClient.invalidateQueries(queryKeys.streak());
    },
  });
};

export const useClaimStreakGoalMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<StreakClaimResponse, AxiosError<StreakErrorBody>, void>({
    mutationFn: async () =>
      (await axios.post<StreakClaimResponse>('/api/user/streak/goal/claim')).data,
    onSuccess: async response => {
      const previous = queryClient.getQueryData<StreakSummary>(
        queryKeys.streak(),
      );

      if (previous !== undefined) {
        queryClient.setQueryData<StreakSummary>(queryKeys.streak(), {
          ...previous,
          goal: null,
        });
      }
      void logAnalyticsEvent('streak_goal_claimed', {
        goal_days: response.goal.target_days,
        reward_babz: response.babz_awarded,
      });
      await Promise.all([
        queryClient.invalidateQueries(queryKeys.wallet()),
        queryClient.invalidateQueries(queryKeys.streak()),
      ]);
    },
    onError: error => {
      reportError('Fail to claim streak goal', error);
      void queryClient.invalidateQueries(queryKeys.streak());
    },
  });
};

export const useGrantStarterFreeze = () => {
  const queryClient = useQueryClient();

  return useMutation<StarterFreezeResponse, AxiosError, void>({
    mutationFn: async () =>
      (await axios.post<StarterFreezeResponse>('/api/user/streak/freezes/starter')).data,
    onSuccess: async response => {
      if (response.granted) {
        void logAnalyticsEvent('streak_freeze_starter_granted', {});
      }
      await Promise.all([
        queryClient.invalidateQueries(queryKeys.wallet()),
        queryClient.invalidateQueries(queryKeys.streak()),
      ]);
    },
  });
};
